import { SortOrder } from 'types/general.types';
import {
  BodyRow,
  ColumnType,
  RowType,
  TableListProps,
} from 'app/components/ChakraTableList';
import React from 'react';
import { openURL } from 'app/utils/utils';
import { SortParams } from 'types/generic.types';
import { rowHeights, subheaderWhitespace } from './constants';
import { TableListLoadingOptions } from './types';

const isSortOrder = (sortOrder: string | null): boolean =>
  sortOrder ? ['asc', 'desc'].includes(sortOrder) : false;

export const getSortParams = <SortBy>(
  urlParams: URLSearchParams,
  initialSortParams: SortParams<SortBy>,
  isSortBy: (sortBy: string | null) => boolean
): SortParams<SortBy> => {
  const sortBy = urlParams.get('sortBy');
  const sortOrder = urlParams.get('sortOrder');
  return {
    sortBy: isSortBy(sortBy)
      ? ((sortBy as unknown) as SortBy)
      : initialSortParams.sortBy,
    sortOrder: isSortOrder(sortOrder)
      ? (sortOrder as SortOrder)
      : initialSortParams.sortOrder,
  };
};

export const getSubheaderRow = (row: BodyRow) => row.type === RowType.SUBHEADER;

export const getRowSum = (acc: number, row: BodyRow) =>
  acc + (row?.height || 0);

export const getCustomRow = (row: BodyRow) =>
  row.type === RowType.CUSTOM_CONTENT;

const getSubHeaderRowHeight = (row: BodyRow) =>
  row.height
    ? ((row.height + subheaderWhitespace) as number)
    : rowHeights.subheader;

export const getRowHeight = (row: BodyRow, regularRowHeight: number) => {
  if (
    row.type &&
    [RowType.SUBHEADER_LOADER, RowType.SUBHEADER].includes(row.type)
  ) {
    return getSubHeaderRowHeight(row);
  }

  if (row.type === RowType.CUSTOM_CONTENT) {
    return row.height ? row.height : regularRowHeight;
  }
  return regularRowHeight;
};

const areRowsEqual = (prevRows: BodyRow[], currentRows: BodyRow[]) => {
  const rowComparator = (rows: BodyRow[]) =>
    JSON.stringify(
      rows.map((row) => ({
        id: row.id,
        clickable: row.clickable,
        to: row.to,
        type: row.type,
        rowData: row.rowData,
        rows: row.rows?.map((innerRow) => ({
          clickable: innerRow.clickable,
          rowData: innerRow.rowData,
        })),
        children: row.children?.map((children) => ({
          id: children.id,
          clickable: children.clickable,
          to: children.to,
          type: children.type,
          rowData: children.rowData,
        })),
      }))
    );
  return rowComparator(prevRows) === rowComparator(currentRows);
};

const areColumnsEqual = (
  prevColumns: ColumnType[],
  currentColumns: ColumnType[]
) => JSON.stringify(prevColumns) === JSON.stringify(currentColumns);

const areTablePropsEqual = (prev: TableListProps, current: TableListProps) =>
  prev.className === current.className &&
  prev.onRowClick === current.onRowClick &&
  prev.loadingOptions?.loading === current.loadingOptions?.loading &&
  prev.loadingOptions?.loadingMore === current.loadingOptions?.loadingMore;

const isLoading = (prev: TableListProps, current: TableListProps) =>
  prev.loadingOptions?.loading && current.loadingOptions?.loading;

export const areComponentPropsEqual = (
  prev: TableListProps,
  current: TableListProps
) => {
  if (isLoading(prev, current)) {
    return true;
  }

  if (!current.memoizeList) {
    return prev === current;
  }

  return (
    areRowsEqual(prev.rows, current.rows) &&
    areColumnsEqual(prev.columns, current.columns) &&
    areTablePropsEqual(prev, current)
  );
};

export const isAtBottom = ({
  currentTarget,
}: React.UIEvent<HTMLDivElement>): boolean => {
  const TRIGGER_OFFSET = 10;
  return (
    currentTarget.scrollTop + TRIGGER_OFFSET >=
    currentTarget.scrollHeight - currentTarget.clientHeight
  );
};

export const getRowsData = (
  rows: BodyRow[],
  columns: ColumnType[],
  subHeaderLoader: JSX.Element,
  loadingOptions?: TableListLoadingOptions
) => {
  const {
    loading = false,
    loadingMore = false,
    repeat = 6,
    loader,
    withSubheader,
  } = loadingOptions || {};
  const loadingRows = Array.from({
    length: repeat + (withSubheader ? 1 : 0),
  }).map((_, i) => {
    if (withSubheader && i === 0) {
      return {
        type: RowType.SUBHEADER_LOADER,
        [columns[0]?.key]: subHeaderLoader,
        loading: true,
      };
    }
    return { ...loader, loading: true };
  }) as BodyRow[];

  if (loading) {
    return loadingRows;
  }

  if (loadingMore) {
    return [...rows, ...loadingRows];
  }

  return rows;
};

export const getOpenInNewTabMenuOption = (url: string) => [
  {
    title: 'Open in New Tab',
    value: 'NEW_TAB',
    onClick: () => {
      openURL(url, {
        newTab: true,
      });
    },
  },
];

export const getVariant = (
  loading: boolean,
  scrollable?: boolean,
  variant?: 'loading' | 'scrollable' | 'playerPlaylist'
) => {
  if (loading) {
    return 'loading';
  }
  if (scrollable) {
    return 'scrollable';
  }
  if (variant) {
    return variant;
  }
  return undefined;
};
